/**
 * Generic API Client
 * Platform-agnostic HTTP client with Auth0 token integration
 *
 * Features:
 * - Automatic token refresh on 401
 * - Request queuing during token refresh
 * - Retry logic with exponential backoff
 * - Typed error handling
 * - Injectable dependencies (logger, connectivity check)
 *
 * @package @rekindle/shared
 */

import {
  ApiError,
  UnauthorizedError,
  ForbiddenError,
  NotFoundError,
  ValidationError,
  NetworkError,
  TimeoutError,
  OfflineError,
  UsageLimitError,
  type ApiErrorResponse,
} from './errors';
import type {
  RequestConfig,
  ApiClientConfig,
  ApiClientDependencies,
  RefreshState,
} from './types';

// Default logger (no-op in production, can be overridden)
const defaultLogger = {
  log: (..._args: unknown[]) => {},
  warn: (..._args: unknown[]) => {},
  error: (..._args: unknown[]) => {},
};

function isFetchFailure(error: unknown): error is Error {
  return error instanceof Error && (
    error.message === 'Network request failed' || error.message === 'Failed to fetch' ||
    error.message.startsWith('fetch failed: ') || error.name === 'TypeError'
  );
}

class RequestCancelledError extends Error {
  constructor() {
    super('Request is no longer current');
    this.name = 'AbortError';
  }
}

function assertCurrentRequest(config: RequestConfig): void {
  if (config.isCurrent && !config.isCurrent()) throw new RequestCancelledError();
}

export class ApiClient {
  private config: ApiClientConfig;
  private deps: Required<ApiClientDependencies>;
  private refreshState: RefreshState;
  private errorListeners = new Set<(error: Error) => void>();

  /** Observe final request failures without replacing the authentication callbacks. */
  subscribeErrors(listener: (error: Error) => void): () => void {
    this.errorListeners.add(listener);
    return () => { this.errorListeners.delete(listener); };
  }

  // Minimum time between refresh attempts (prevents rapid retries)
  private static readonly MIN_REFRESH_INTERVAL_MS = 5000;

  constructor(baseURL: string, deps?: ApiClientDependencies) {
    this.config = {
      baseURL,
      timeout: 30000,
      getAccessToken: null,
      forceRefreshToken: null,
      onUnauthorized: undefined,
      onError: undefined,
    };

    this.deps = {
      isOnline: deps?.isOnline ?? (async () => true),
      logger: deps?.logger ?? defaultLogger,
    };

    this.refreshState = {
      isRefreshing: false,
      refreshPromise: null,
      lastRefreshAttempt: 0,
    };

    this.deps.logger.log(`🌐 API Client initialized: ${this.config.baseURL}`);
  }

  /**
   * Initialize with Auth0 token getter and refresh capability
   * @param getAccessToken - Function to get current access token (may use cached token)
   * @param forceRefreshToken - Function to force token refresh (bypasses cache)
   * @param onUnauthorized - Called only when refresh token is expired (true logout needed)
   * @param onError - Called for general API errors
   */
  initialize(
    getAccessToken: () => Promise<string | null>,
    forceRefreshToken: () => Promise<string | null>,
    onUnauthorized?: () => void,
    onError?: (error: Error) => void
  ): void {
    this.config.getAccessToken = getAccessToken;
    this.config.forceRefreshToken = forceRefreshToken;
    this.config.onUnauthorized = onUnauthorized;
    this.config.onError = onError;
    this.deps.logger.log('✅ API Client configured with auth and token refresh');
  }

  /**
   * Attempt to refresh the access token
   * Uses singleton pattern to prevent multiple concurrent refresh attempts
   * @returns New access token or null if refresh failed
   */
  private async refreshAccessToken(): Promise<string | null> {
    const { logger } = this.deps;

    // If already refreshing, wait for the existing refresh to complete
    if (this.refreshState.isRefreshing && this.refreshState.refreshPromise) {
      logger.log('🔄 Token refresh already in progress, waiting...');
      return this.refreshState.refreshPromise;
    }

    // Prevent rapid refresh attempts
    const now = Date.now();
    const timeSinceLastRefresh = now - this.refreshState.lastRefreshAttempt;
    if (timeSinceLastRefresh < ApiClient.MIN_REFRESH_INTERVAL_MS) {
      logger.log('⏳ Too soon since last refresh attempt, skipping');
      return null;
    }

    // No refresh function configured
    if (!this.config.forceRefreshToken) {
      logger.warn('⚠️ No forceRefreshToken function configured');
      return null;
    }

    // Start refresh
    this.refreshState.isRefreshing = true;
    this.refreshState.lastRefreshAttempt = now;

    logger.log('🔄 Attempting to refresh access token...');

    this.refreshState.refreshPromise = (async () => {
      try {
        const newToken = await this.config.forceRefreshToken!();
        if (newToken) {
          logger.log('✅ Token refreshed successfully');
          return newToken;
        } else {
          // Refresh token is expired - onUnauthorized callback will handle logout
          logger.log('🔒 Token refresh returned null - refresh token expired');
          return null;
        }
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        logger.error('❌ Token refresh failed:', errorMessage);
        return null;
      } finally {
        this.refreshState.isRefreshing = false;
        this.refreshState.refreshPromise = null;
      }
    })();

    return this.refreshState.refreshPromise;
  }

  /**
   * Core request method with automatic token refresh on 401
   */
  private async request<T>(endpoint: string, config: RequestConfig): Promise<T> {
    try {
      return await this.performRequest<T>(endpoint, config);
    } catch (failure) {
      assertCurrentRequest(config);
      if (failure instanceof RequestCancelledError) throw failure;
      const error = failure instanceof Error ? failure : new Error(String(failure));
      // Observers must never alter the result or cause a successful write to replay.
      for (const listener of [this.config.onError, ...this.errorListeners]) {
        try { listener?.(error); } catch { /* Diagnostics are best effort. */ }
      }
      throw error;
    }
  }

  private async performRequest<T>(endpoint: string, config: RequestConfig): Promise<T> {
    const { logger } = this.deps;
    const {
      method,
      headers = {},
      body,
      timeout = this.config.timeout,
      skipAuth = false,
      retries = 1,
      _isRetryAfterRefresh = false,
    } = config;

    assertCurrentRequest(config);
    // Check network connectivity
    const online = await this.deps.isOnline();
    assertCurrentRequest(config);
    if (!online) {
      throw new OfflineError();
    }

    const url = `${this.config.baseURL}${endpoint}`;

    // Build headers
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
    };

    // Add auth token if available and not skipped
    if (!skipAuth && this.config.getAccessToken) {
      try {
        const token = await this.config.getAccessToken();
        assertCurrentRequest(config);
        if (token) {
          requestHeaders['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        assertCurrentRequest(config);
        logger.warn('⚠️ Failed to get access token:', error);
      }
    }

    const logEndpoint = endpoint.split('?')[0];
    logger.log(`📡 ${method} ${logEndpoint}`);

    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= retries; attempt++) {
      assertCurrentRequest(config);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      try {
        const response = await fetch(url, {
          method,
          headers: requestHeaders,
          body: body ? JSON.stringify(body) : undefined,
          signal: controller.signal,
        });
        assertCurrentRequest(config);
        if (controller.signal.aborted) throw new TimeoutError();

        logger.log(`📬 Response: ${response.status}`);

        // Handle error responses
        if (!response.ok) {
          const errorData = await this.parseErrorResponse(response, controller.signal);
          assertCurrentRequest(config);
          if (controller.signal.aborted) throw new TimeoutError();
          throw this.createError(response.status, errorData);
        }

        // Handle 204 No Content
        if (response.status === 204) {
          return null as T;
        }

        const data = await response.json();
        assertCurrentRequest(config);
        if (controller.signal.aborted) throw new TimeoutError();

        return data as T;
      } catch (error: unknown) {
        assertCurrentRequest(config);
        if (error instanceof RequestCancelledError) throw error;
        // Expo's native fetch rejects cancellation as a plain Error, not an
        // AbortError. Our own deadline is authoritative on every platform,
        // including timeouts while reading an error response body.
        if (controller.signal.aborted) {
          lastError = new TimeoutError();
          break;
        }
        lastError = error as Error;

        // Handle 401 Unauthorized - attempt token refresh before giving up
        if (error instanceof UnauthorizedError && !_isRetryAfterRefresh && !skipAuth) {
          logger.log('🔐 Received 401 - attempting token refresh...');

          const newToken = await this.refreshAccessToken();
          assertCurrentRequest(config);

          if (newToken) {
            // Token refreshed successfully - retry the request with new token
            logger.log('🔄 Retrying request with refreshed token...');
            return this.performRequest<T>(endpoint, {
              ...config,
              _isRetryAfterRefresh: true, // Prevent infinite refresh loops
            });
          } else {
            // Token refresh failed - this means refresh token is expired
            // Now we should trigger the unauthorized callback (true logout)
            logger.log('❌ Token refresh failed - session truly expired');
            if (this.config.onUnauthorized) {
              this.config.onUnauthorized();
            }
            break;
          }
        }

        // Don't retry certain errors (except 401 which is handled above)
        if (
          error instanceof ForbiddenError ||
          error instanceof ValidationError ||
          error instanceof NotFoundError ||
          error instanceof UsageLimitError ||
          (error instanceof ApiError && error.statusCode === 429) ||
          (error as Error).name === 'AbortError'
        ) {
          break;
        }

        // Also break on 401 if we already tried refresh (handled above)
        if (error instanceof UnauthorizedError) {
          break;
        }

        // Retry on network errors
        if (attempt < retries) {
          logger.log(`🔄 Retry ${attempt}/${retries} for ${logEndpoint}`);
          await this.delay(1000 * attempt); // Exponential backoff
          continue;
        }
      } finally {
        clearTimeout(timeoutId);
      }
    }

    // Handle final error
    if (lastError?.name === 'AbortError') {
      throw new TimeoutError();
    }

    if (lastError instanceof ApiError) {
      throw lastError;
    }

    // Handle network errors (fetch failures)
    // Different messages across platforms:
    // - Browser: "Failed to fetch"
    // - React Native: "Network request failed"
    // - General: TypeError when network is unreachable
    if (isFetchFailure(lastError)) {
      throw new NetworkError();
    }

    throw lastError || new ApiError('Unknown error', 500);
  }

  private async parseErrorResponse(response: Response, signal: AbortSignal): Promise<ApiErrorResponse> {
    try {
      return await response.json();
    } catch (error) {
      // A timed-out error body is a transport failure, not evidence that a
      // session expired. Preserve it before interpreting the HTTP status.
      if (signal.aborted || isFetchFailure(error) || (error instanceof Error && error.name === 'AbortError')) throw error;
      return { error: response.statusText || 'Unknown error' };
    }
  }

  private createError(status: number, data: ApiErrorResponse): ApiError {
    const normalized = ApiError.fromResponse(data, status);
    const { message, details } = normalized;

    switch (status) {
      case 400:
        return new ValidationError(message, details);
      case 401:
        return new UnauthorizedError(message, details);
      case 403:
        return new ForbiddenError(message, details);
      case 404:
        return new NotFoundError(message, details);
      case 429:
        // Request throttling is not evidence of an exhausted plan allowance.
        if (normalized.code !== 'USAGE_LIMIT_EXCEEDED') return normalized;
        return new UsageLimitError(
          message,
          details as {
            currentUsage?: number;
            limit?: number;
            planTier?: string;
            feature?: string;
          }
        );
      default:
        return ApiError.fromResponse(data, status);
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // ============ Public HTTP Methods ============

  get<T>(endpoint: string, config?: Partial<RequestConfig>): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', ...config });
  }

  post<T>(endpoint: string, body?: unknown, config?: Partial<RequestConfig>): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', body, ...config });
  }

  put<T>(endpoint: string, body?: unknown, config?: Partial<RequestConfig>): Promise<T> {
    return this.request<T>(endpoint, { method: 'PUT', body, ...config });
  }

  patch<T>(endpoint: string, body?: unknown, config?: Partial<RequestConfig>): Promise<T> {
    return this.request<T>(endpoint, { method: 'PATCH', body, ...config });
  }

  delete<T>(endpoint: string, config?: Partial<RequestConfig>): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', ...config });
  }

  // ============ Utility Methods ============

  getBaseURL(): string {
    return this.config.baseURL;
  }

  isInitialized(): boolean {
    return this.config.getAccessToken !== null;
  }

  /**
   * Get the current access token
   * Useful for manual fetch requests (e.g., SSE streaming)
   */
  async getAccessToken(): Promise<string | null> {
    if (!this.config.getAccessToken) {
      return null;
    }
    return this.config.getAccessToken();
  }
}
